import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Search, Filter, MapPin, SlidersHorizontal, X, Mountain, PartyPopper, Utensils, Landmark, Leaf, Dumbbell, HeartPulse, Moon, Info, Caravan, Paintbrush, Car, Home, Backpack, Plane, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { ExperienceCard, ExperienceCardSkeleton } from '@/components/ExperienceCard';
import { experiencesApi } from '@/api';

const CATEGORY_ICONS = {
  adventure: Mountain,
  fun: PartyPopper,
  food: Utensils,
  culture: Landmark,
  nature: Leaf,
  sport: Dumbbell,
  healing: HeartPulse,
  nightlife: Moon,
  vanlife: Caravan,
  artisanat: Paintbrush,
  local_events: CalendarDays
};

const ExplorePage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedCategories, setSelectedCategories] = useState([]);  // Multiple categories
  const [location, setLocation] = useState(searchParams.get('location') || '');
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [selectedServices, setSelectedServices] = useState([]);  // Guide services filter

  const categories = [
    { id: 'adventure', name: t('category.adventure') },
    { id: 'fun', name: t('category.fun') },
    { id: 'food', name: t('category.food') },
    { id: 'culture', name: t('category.culture') },
    { id: 'sport', name: t('category.sport') },
    { id: 'healing', name: t('category.healing') },
    { id: 'nightlife', name: t('category.nightlife') },
    { id: 'vanlife', name: t('category.vanlife') },
    { id: 'artisanat', name: t('category.artisanat') },
    { id: 'local_events', name: t('category.local_events') }
  ];

  const guideServices = [
    { id: 'vehicle', name: i18n.language === 'fr' ? 'Véhicule' : 'Vehicle', icon: Car },
    { id: 'accommodation', name: i18n.language === 'fr' ? 'Hébergement' : 'Accommodation', icon: Home },
    { id: 'equipment', name: i18n.language === 'fr' ? 'Équipement' : 'Equipment', icon: Backpack },
    { id: 'meal', name: i18n.language === 'fr' ? 'Repas' : 'Meals', icon: Utensils },
    { id: 'transfer', name: i18n.language === 'fr' ? 'Transfert' : 'Transfer', icon: Plane }
  ];

  const toggleCategory = (catId) => {
    setSelectedCategories(prev => 
      prev.includes(catId)
        ? prev.filter(c => c !== catId)
        : [...prev, catId]
    );
  };

  const toggleService = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId)
        ? prev.filter(s => s !== serviceId)
        : [...prev, serviceId]
    );
  };

  const fetchExperiences = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (selectedCategories.length > 0) params.categories = selectedCategories.join(',');
      if (location) params.location = location;
      if (priceRange[0] > 0) params.min_price = priceRange[0];
      if (priceRange[1] < 500) params.max_price = priceRange[1];
      if (selectedServices.length > 0) params.guide_services = selectedServices.join(',');
      
      const data = await experiencesApi.getAll(params);
      setExperiences(data.experiences);
      setTotal(data.total);
    } catch (error) {
      console.error('Error fetching experiences:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize from URL params on mount
  useEffect(() => {
    const initialCategory = searchParams.get('category');
    const initialSearch = searchParams.get('search');
    if (initialCategory) {
      setSelectedCategories([initialCategory]);
    }
    if (initialSearch) {
      setSearch(initialSearch);
    }
  }, []); // Only on mount

  // Fetch when filters change
  useEffect(() => {
    fetchExperiences();
  }, [selectedCategories, selectedServices]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchExperiences();
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedCategories([]);
    setSelectedServices([]);
    setLocation('');
    setPriceRange([0, 500]);
    setSearchParams({});
    fetchExperiences();
  };

  const hasActiveFilters = search || selectedCategories.length > 0 || selectedServices.length > 0 || location || priceRange[0] > 0 || priceRange[1] < 500;

  return (
    <main className="min-h-screen pt-20 pb-12" data-testid="explore-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="py-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{t('nav.explore')}</h1>
          <p className="text-muted-foreground">
            {total} {i18n.language === 'fr' ? 'expériences disponibles' : 'experiences available'}
          </p>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t('hero.search_placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" data-testid="search-btn">
              <Search className="w-5 h-5" />
            </Button>
          </form>
          
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="md:hidden"
            data-testid="filter-toggle"
          >
            <SlidersHorizontal className="w-5 h-5 mr-2" />
            {t('common.filter')}
          </Button>
        </div>

        <div className="flex gap-8">
          {/* Sidebar Filters - Desktop */}
          <aside className={`w-64 shrink-0 ${showFilters ? 'block' : 'hidden'} md:block`}>
            <div className="sticky top-24 space-y-6 p-4 bg-card rounded-2xl border">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{t('common.filter')}</h3>
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="w-4 h-4 mr-1" /> Clear
                  </Button>
                )}
              </div>

              {/* Category - Multiple Selection */}
              <div className="space-y-3">
                <label className="text-sm font-medium">
                  {i18n.language === 'fr' ? 'Catégories' : 'Categories'}
                </label>
                <div className="space-y-2">
                  {categories.map((cat) => {
                    const Icon = CATEGORY_ICONS[cat.id];
                    const isSelected = selectedCategories.includes(cat.id);
                    return (
                      <label
                        key={cat.id}
                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all ${
                          isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleCategory(cat.id)}
                          className="rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <Icon className="w-4 h-4" />
                        <span className="text-sm">{cat.name}</span>
                      </label>
                    );
                  })}
                </div>
                {selectedCategories.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setSelectedCategories([])}
                    className="text-xs"
                  >
                    {i18n.language === 'fr' ? 'Effacer la sélection' : 'Clear selection'}
                  </Button>
                )}
              </div>

              {/* Location */}
              <div className="space-y-3">
                <label className="text-sm font-medium">Lieu</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Rome, Paris..."
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="pl-9"
                    data-testid="location-input"
                  />
                </div>
              </div>

              {/* Guide Services Filter */}
              <div className="space-y-3">
                <label className="text-sm font-medium">
                  {i18n.language === 'fr' ? 'Services du guide' : 'Guide Services'}
                </label>
                <div className="space-y-2">
                  {guideServices.map((service) => {
                    const Icon = service.icon;
                    const isSelected = selectedServices.includes(service.id);
                    return (
                      <label
                        key={service.id}
                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all ${
                          isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleService(service.id)}
                          className="rounded border-gray-300 text-primary focus:ring-primary"
                          data-testid={`service-filter-${service.id}`}
                        />
                        <Icon className="w-4 h-4" />
                        <span className="text-sm">{service.name}</span>
                      </label>
                    );
                  })}
                </div>
                {selectedServices.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setSelectedServices([])}
                    className="text-xs"
                  >
                    {i18n.language === 'fr' ? 'Effacer la sélection' : 'Clear selection'}
                  </Button>
                )}
              </div>

              {/* Price Range */}
              <div className="space-y-3">
                <label className="text-sm font-medium">Prix (€)</label>
                <Slider
                  value={priceRange}
                  onValueChange={setPriceRange}
                  max={500}
                  step={10}
                  className="mt-2"
                  data-testid="price-slider"
                />
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>{priceRange[0]}€</span>
                  <span>{priceRange[1]}€{priceRange[1] === 500 && '+'}</span>
                </div>
              </div>

              <Button onClick={fetchExperiences} className="w-full" data-testid="apply-filters">
                Appliquer les filtres
              </Button>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Category Pills - Multiple Selection */}
            <div className="flex flex-wrap gap-2 mb-6 overflow-x-auto pb-2">
              <Button
                variant={selectedCategories.length === 0 ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategories([])}
                className="rounded-full"
              >
                {i18n.language === 'fr' ? 'Tout' : 'All'}
              </Button>
              {categories.map((cat) => {
                const Icon = CATEGORY_ICONS[cat.id];
                const isSelected = selectedCategories.includes(cat.id);
                return (
                  <Button
                    key={cat.id}
                    variant={isSelected ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleCategory(cat.id)}
                    className={`rounded-full ${isSelected ? 'ring-2 ring-primary ring-offset-1' : ''}`}
                    data-testid={`category-pill-${cat.id}`}
                  >
                    <Icon className="w-4 h-4 mr-1" />
                    {cat.name}
                  </Button>
                );
              })}
            </div>
            
            {/* Selected categories info */}
            {selectedCategories.length > 1 && (
              <div className="mb-4 text-sm text-muted-foreground bg-muted/50 rounded-lg p-2">
                {i18n.language === 'fr' 
                  ? `Recherche dans ${selectedCategories.length} catégories : `
                  : `Searching in ${selectedCategories.length} categories: `}
                <span className="font-medium text-foreground">
                  {selectedCategories.map(c => categories.find(cat => cat.id === c)?.name).join(' + ')}
                </span>
              </div>
            )}

            {/* Results Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {loading ? (
                [...Array(6)].map((_, i) => <ExperienceCardSkeleton key={i} />)
              ) : experiences.length > 0 ? (
                experiences.map((exp) => (
                  <motion.div
                    key={exp.experience_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <ExperienceCard experience={exp} />
                  </motion.div>
                ))
              ) : (
                <div className="col-span-full text-center py-16">
                  <p className="text-xl text-muted-foreground mb-4">
                    {i18n.language === 'fr' 
                      ? 'Aucune expérience trouvée' 
                      : 'No experiences found'}
                  </p>
                  <Button variant="outline" onClick={clearFilters}>
                    {i18n.language === 'fr' ? 'Effacer les filtres' : 'Clear filters'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ExplorePage;
